import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Log {
  id: number
  header: string | null
  log_date: string | null
  scheduled_date: string | null
  log_type: string | null
  user: string | null
  status: number
  urgent: boolean
  completed: boolean
}

interface Props {
  customer: Customer
  logs: Log[]
}

export default function Index({ customer, logs }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Logs", href: `/customers/${customer.id}/logs` },
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this log?")) {
      router.delete(`/customers/${customer.id}/logs/${id}`)
    }
  }

  const handleComplete = (id: number) => {
    if (confirm("Mark this log as complete?")) {
      router.patch(`/customers/${customer.id}/logs/${id}/complete`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Logs - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Logs for {customer.display_name}
          </h1>
          <Link
            href={`/customers/${customer.id}/logs/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Log
          </Link>
        </div>

        {logs.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Subject
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  User
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {formatDate(log.log_date)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/logs/${log.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      {log.header ?? "(No subject)"}
                    </Link>
                    {log.urgent && (
                      <span className="bg-hpi-red/20 text-hpi-red ml-2 rounded px-2 py-0.5 text-xs">
                        Urgent
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {log.log_type}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {log.user}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {log.completed ? (
                      <span className="bg-hpi-dark-green/20 text-hpi-dark-green rounded px-2 py-0.5 text-xs">
                        Complete
                      </span>
                    ) : log.scheduled_date ? (
                      <span className="bg-hpi-yellow/30 text-hpi-dark-gray rounded px-2 py-0.5 text-xs">
                        Scheduled {formatDate(log.scheduled_date)}
                      </span>
                    ) : (
                      <span className="bg-hpi-pale-gray text-hpi-dark-gray rounded px-2 py-0.5 text-xs">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/logs/${log.id}/edit`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      Edit
                    </Link>
                    {!log.completed && (
                      <button
                        onClick={() => handleComplete(log.id)}
                        className="text-hpi-dark-green mr-2 hover:underline"
                      >
                        Complete
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(log.id)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No logs found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Customer
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
